import {
    DECLARATION_ACCEPTED_FIELD,
    getSectionTitle,
    SUBSTANCES_FIELD,
} from "./fields";
import { CatalogAttribute, isAttributeRequired } from "./fallbackCatalog";

// The harmful-substances section used to be gated on a confirmed contamination
// and mirrored live by `isSubstancesSectionActive()`. Rev. 2 of the form asks
// for it unconditionally ("must be filled out - even if only air exposure"), so
// the gate is gone and the section is always rendered. `validity`'s
// active/inactive lists stay the backend's word on what was asked; nothing in
// the current catalog puts anything on the inactive one.

export interface ValidityLike {
    isValid: boolean;
    /** The backend's own verdict: complete *and* accepted. Authoritative. */
    isPrintable?: boolean | null;
    activeQuestionIndexList?: ReadonlyArray<string> | null;
    missingQuestionIndexList?: ReadonlyArray<string> | null;
    inactiveQuestionIndexList?: ReadonlyArray<string> | null;
}

export interface PrintReadiness {
    /** The PDF is offered only when this is true — `file` is null otherwise. */
    ready: boolean;
    missingCount: number;
    acceptedMissing: boolean;
}

/**
 * Print gating. The backend publishes the verdict as `validity.isPrintable`
 * (`isValid` AND the legally binding declaration accepted); the conjunction is
 * derived here only where that field is absent, e.g. from a cached result
 * written before it existed. `??`, not `||`: a genuine `false` from the backend
 * must not be overridden. The consent tick is deliberately not part of
 * `isValid`, so it is reported separately for the status line.
 */
export const getPrintReadiness = (
    validity: ValidityLike | null | undefined,
    declarationAccepted: boolean,
): PrintReadiness => ({
    ready: validity?.isPrintable ?? (!!validity?.isValid && declarationAccepted),
    missingCount: validity?.missingQuestionIndexList?.length ?? 0,
    acceptedMissing: !declarationAccepted,
});

export interface AttributeLike {
    index: string;
    categories?: ReadonlyArray<string | null> | null;
}

export interface AttributeSection<A extends AttributeLike> {
    key: string;
    title: string;
    attributes: A[];
}

export const UNCATEGORIZED_SECTION_KEY = 'other';

/**
 * Groups the catalog by its first category, keeping the catalog's order of
 * appearance — the backend publishes sections in render order, so no sorting.
 */
export const groupAttributesBySection = <A extends AttributeLike>(
    attributes: ReadonlyArray<A | null | undefined> | null | undefined,
): AttributeSection<A>[] => {
    const sections = new Map<string, AttributeSection<A>>();
    (attributes ?? []).forEach((attribute) => {
        if (!attribute) return;
        const key = attribute.categories?.find((category): category is string => !!category)
            ?? UNCATEGORIZED_SECTION_KEY;
        if (!sections.has(key)) {
            sections.set(key, { key, title: getSectionTitle(key), attributes: [] });
        }
        sections.get(key)!.attributes.push(attribute);
    });
    return Array.from(sections.values());
};

const hasSubstanceRow = (value: unknown): boolean =>
    Array.isArray(value)
    && value.some((row) => !!row && typeof row === 'object'
        && Object.values(row as Record<string, unknown>).some((cell) => typeof cell === 'string' && cell.trim() !== ''));

/**
 * Mirrors the backend: "No" (false) is an answer, blank is not. Two exceptions:
 * the consent box counts only when ticked, and the substances table only with
 * at least one filled row.
 */
export const isQuestionAnswered = (index: string, values: Record<string, unknown>): boolean => {
    const value = values[index];
    if (index === DECLARATION_ACCEPTED_FIELD) return value === true;
    if (index === SUBSTANCES_FIELD) return hasSubstanceRow(value);
    if (typeof value === 'boolean') return true;
    if (typeof value === 'number') return true;
    if (typeof value === 'string') return value.trim() !== '';
    return false;
};

/**
 * Whether a question must be answered for the form to count as complete: the
 * catalog's `required` rule plus the consent box, which gates printing by
 * definition and is therefore never a catalog rule. The substances table is
 * listed too, though the catalog now marks it required as well — the explicit
 * case survives the fallback catalog and any older cached one. The additional
 * information stays optional here; the backend's save-time REQUIRED rule for
 * "other substances = Yes" is mirrored in the diff builder only (product
 * decision 2026-09-16).
 */
export const isRequiredForCompletion = (attribute: CatalogAttribute): boolean =>
    attribute.index === DECLARATION_ACCEPTED_FIELD
    || attribute.index === SUBSTANCES_FIELD
    || isAttributeRequired(attribute);

/** Required questions of a section still unanswered in the current form values. */
export const getOpenQuestionIndices = (
    attributes: ReadonlyArray<CatalogAttribute>,
    values: Record<string, unknown>,
    liveActive: boolean = true,
): string[] =>
    liveActive
        ? attributes.filter((a) => isRequiredForCompletion(a) && !isQuestionAnswered(a.index, values)).map((a) => a.index)
        : [];

export interface SectionProgress {
    key: string;
    title: string;
    number: number;
    liveActive: boolean;
    status: SectionStatus;
    openIndices: string[];
}

export interface FormProgress {
    sections: SectionProgress[];
    /** Required, active and still unanswered questions across all sections. */
    totalOpen: number;
    /** 0–100, answered share of the required active questions. */
    percent: number;
}

/**
 * Live completeness of the whole form from the current values (updates while
 * the user answers; the backend's `validity` confirms it after each save).
 */
export const getFormProgress = (
    sections: ReadonlyArray<AttributeSection<CatalogAttribute>>,
    values: Record<string, unknown>,
    isSectionActive: (key: string) => boolean,
): FormProgress => {
    let requiredActive = 0;
    let totalOpen = 0;
    const progress = sections.map((section, i) => {
        const liveActive = isSectionActive(section.key);
        const openIndices = getOpenQuestionIndices(section.attributes, values, liveActive);
        if (liveActive) {
            requiredActive += section.attributes.filter(isRequiredForCompletion).length;
        }
        totalOpen += openIndices.length;
        return {
            key: section.key,
            title: section.title,
            number: i + 1,
            liveActive,
            status: getLiveSectionStatus(openIndices.length, liveActive),
            openIndices,
        };
    });
    const percent = requiredActive === 0 ? 100 : Math.round(((requiredActive - totalOpen) / requiredActive) * 100);
    return { sections: progress, totalOpen, percent };
};

export type SectionStatus = 'inactive' | 'missing' | 'complete' | 'open';

export const getLiveSectionStatus = (openCount: number, liveActive: boolean): SectionStatus =>
    !liveActive ? 'inactive' : openCount > 0 ? 'missing' : 'complete';

/**
 * Status chip of a section: `inactive` when gated off (live rule or backend),
 * `missing` when a required active question is unanswered, `complete` when
 * every active question is answered, `open` when no validity is known yet.
 */
export const getSectionStatus = (
    indices: ReadonlyArray<string>,
    validity: ValidityLike | null | undefined,
    liveActive: boolean = true,
): SectionStatus => {
    if (!liveActive) return 'inactive';
    if (!validity) return 'open';
    const inactive = new Set(validity.inactiveQuestionIndexList ?? []);
    const missing = new Set(validity.missingQuestionIndexList ?? []);
    const active = new Set(validity.activeQuestionIndexList ?? []);
    if (indices.length > 0 && indices.every((index) => inactive.has(index))) return 'inactive';
    if (indices.some((index) => missing.has(index))) return 'missing';
    if (indices.some((index) => active.has(index))) return 'complete';
    return 'open';
};
